import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Badge, Carousel, Column, Container, HeroVertical, Row } from '@pella/sash-framework';
import { Breadcrumbs, CTA, Image, Layout, SEO } from '../../components';
import { isValidArray } from '../../components/utilities/validations';
import { filterContentByTags } from '../../components/utilities/functions';

import LocalNavTop from '../../components/common/local-nav-top';
import CardSection from '../../components/common/card-section';
import DynamicComponent from '../../components/common/dynamic-component';
import ProjectGalleryWithFilters from '../../components/template/project-landing/project-gallery-with-filters';
import InspirationGrid from '../../components/common/inspiration-grid';
import { HoverEmbed } from '../../components/third-party/hover';
// import HoverBannerLarge from '../../components/third-party/hover/large-banner';

import './index.css';

const ProjectLanding = (props) => {
	const pageData = props.data.contentstackProjectLanding;
	const inspirationEntries = props.data.inspirationEntries;
	const projectEntries = props.data.allProjects;

	return (
		<Layout
			id='template-ProjectLanding'
			hideLeadGen
			pageTitle={pageData.seo?.title}
		>
			{isValidArray(pageData?.anchor_cta) && (
				<LocalNavTop
					linksArray={pageData?.anchor_cta}
					currentUrl={pageData.url}
					className='text-left'
					isAnchors={false}
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{isValidArray(pageData.hero?.image) && (
				<HeroVertical
					image={pageData.hero.image[0].image?.gatsbyImageData?.images?.fallback}
					heading={pageData.hero.heading}
					content={pageData.hero.description}
					contentWrapperClass='max-md:relative items-start'
				>
					{isValidArray(pageData.hero.cta) && (
						<div className={`${pageData.hero.cta_placement === 'over-image' ? 'absolute bottom-full left-0 z-2 w-full bg-gradient-to-t from-black to-transparent px-6 py-2.5 md:bottom-0 md:w-[63%] md:px-8 md:py-5 xl:w-8/12 xl:py-8 xl:pl-16' : ''}`}>
							<CTA
								data={pageData.hero.cta[0]}
								size='sm'
								className={pageData.hero.cta_placement === 'over-image' ? '!justify-start !text-left text-white hover:!opacity-100' : 'pl-hero-cta w-fit !justify-start'}
							>
								<span dangerouslySetInnerHTML={{ __html: pageData.hero.cta[0].link.title }} />
							</CTA>
						</div>
					)}
				</HeroVertical>
			)}

			{isValidArray(pageData.page_components) &&
				pageData.page_components.map((block, index) => {
					if (block.cards != null) {
						return (
							<CardSection
								key={index}
								data={block.cards}
							/>
						);
					}

					if (block.dynamic != null) {
						return (
							<DynamicComponent
								key={index}
								data={block.dynamic}
							/>
						);
					}

					if (block.project_grid != null && block.project_grid.grid_type === 'inspiration') {
						return (
							<InspirationGrid
								key={index}
								data={block.project_grid}
								productType={pageData.product_type}
								staticTiles={inspirationEntries}
								baseFilters={block.project_grid.filter_tags}
								popularPicks={pageData.page_components?.find((block) => block.filter_buttons != null)?.filter_buttons}
							/>
						);
					}

					if (block.project_grid != null && block.project_grid.grid_type === 'articles') {
						let articlesByTags = projectEntries.nodes;
						if (isValidArray(block.project_grid.filter_tags)) {
							articlesByTags = filterContentByTags(block.project_grid.filter_tags, projectEntries.nodes, false);
						}

						return (
							<ProjectGalleryWithFilters
								key={index}
								data={block.project_grid}
								projects={articlesByTags}
								productType={pageData.product_type}
							/>
						);
					}

					if (isValidArray(block.manual_cards?.cards)) {
						return (
							<div
								key={index}
								className='block-manual-cards bg-black py-6 md:py-10'
							>
								<Container noPadding>
									<Row
										align='center'
										noGutters
									>
										<Column
											xs={12}
											lg={4}
										>
											<div className='mb-6 px-6 md:px-8 xl:mb-0 xl:pl-16'>
												{block.manual_cards.heading && <h2 className='text-h3 text-white'>{block.manual_cards.heading}</h2>}
												{block.manual_cards.description && <p className='text-body-2 mt-4 text-pretty text-white xl:mt-3 xl:text-base'>{block.manual_cards.description}</p>}

												{isValidArray(block.manual_cards.cta) && (
													<CTA
														data={block.manual_cards.cta[0]}
														className='mt-4 sm:!w-[clamp(200px,312px,100%)] xl:mt-12'
														size='sm'
													/>
												)}
											</div>
										</Column>

										<Column
											xs={12}
											lg={8}
										>
											<Carousel
												slidesToShow={{ xs: 1, md: 2 }}
												slidePadding={16}
												className='before:from-black after:from-black max-xl:after:content-none max-lg:before:content-none'
												dotsClassName='xl:hidden [&_button[aria-current=true]]:before:bg-white [&_button[aria-current=false]]:before:bg-neutral-600'
												showSlideHints
												showArrows
												showDots
											>
												{block.manual_cards.cards.map((card, index) => {
													const isCardLink = isValidArray(card.cta);
													const CardWrapper = isCardLink ? CTA : 'div';

													return (
														<CardWrapper
															key={index}
															className='block h-full bg-white p-4 no-underline'
															data={isCardLink ? card.cta[0] : null}
														>
															{isValidArray(card.image) && (
																<div className='relative'>
																	<Image
																		data={card.image[0]}
																		className='mb-4 !block aspect-[4/3] object-cover md:mb-3 xl:mb-4'
																	/>

																	{card.badge && (
																		<Badge
																			color='primary'
																			className='absolute left-2 top-2'
																		>
																			{card.badge}
																		</Badge>
																	)}
																</div>
															)}

															{card.heading && <h3 className='text-subtitle mb-1 font-bold md:mb-2 xl:mb-4'>{card.heading}</h3>}
															{card.subheading && <p className='text-body-2 text-pretty text-neutral-600'>{card.subheading}</p>}
														</CardWrapper>
													);
												})}
											</Carousel>
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.embed != null) {
						return (
							<div
								key={index}
								id={block.embed.anchor_text}
								className='my-6 xl:my-8'
							>
								<Container>
									<HoverEmbed configId={block.embed.embed_id} />
									{/* <HoverBannerLarge /> */}
								</Container>
							</div>
						);
					}
				})}
		</Layout>
	);
};

ProjectLanding.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default ProjectLanding;

// eslint-disable-next-line react/prop-types -- gatsby head component
export const Head = ({ data }) => <SEO {...data.contentstackProjectLanding.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackProjectLanding(id: { eq: $uid }) {
			url
			breadcrumbs {
				...Breadcrumbs
			}
			product_type
			anchor_cta {
				...CTA
			}
			hero {
				description
				heading
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90)
					}
				}
				cta_placement
				cta {
					...CTA
				}
			}
			page_components {
				embed {
					anchor_text
					embed_id
				}
				cards {
					...CardSection
				}
				project_grid {
					grid_type
					filter_tags
					heading
					all_products {
						filter_tags
						display_name
					}
				}
				dynamic {
					background_hex_value
					heading
					dynamic {
						...Dynamic
					}
				}
				filter_buttons {
					heading
					subheading
					buttons {
						file {
							url
							gatsbyImageData(quality: 90)
						}
						subheading
						heading
						tags_by_category {
							category
							filter_tag
						}
					}
				}
				manual_cards {
					heading
					description
					cta {
						...CTA
					}
					cards {
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
						heading
						subheading
						cta {
							...CTA
						}
						badge
					}
				}
			}
			seo {
				...SEO
			}
		}
		allProjects: allContentstackArticles(filter: { taxonomies: { elemMatch: { term_uid: { in: ["window_projects", "patio_door_projects", "front_door_projects"] } } }, article_type: { ne: "Case Study" } }, sort: { created_at: DESC }) {
			totalCount
			nodes {
				...Article
			}
		}
		inspirationEntries: allContentstackImageDetail(sort: { created_at: DESC }, limit: 14) {
			totalCount
			nodes {
				...ImageDetail
			}
		}
	}
`;
